import React, { useEffect, useState } from 'react';
import { saRequest } from '../../../utils/superAdminAxiosCaller';
import { parseSuperAdminError } from '../../../utils/superAdminErrors';

export interface ImpersonationTarget {
    user_id: number;
    user_email: string | null;
    user_fullname: string;
    company_id: number;
    company_name: string;
    is_tenant_admin: boolean;
}

interface Props {
    target: ImpersonationTarget;
    onClose: () => void;
}

const DURATIONS: Array<{ value: 15 | 30 | 60; label: string }> = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 60, label: '60 min' },
];

/**
 * Super-admin-side kickoff for a read-only impersonation session.
 * On submit: POST /super-admin/companies/{id}/users/{uid}/impersonate,
 * get the OTX, open /impersonation/landing?otx=... in a new tab. The
 * destination tab handles exchange + storage + redirect.
 */
const ImpersonateModal: React.FC<Props> = ({ target, onClose }) => {
    const [reason, setReason] = useState('');
    const [duration, setDuration] = useState<15 | 30 | 60>(15);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Desktop-only in v1 — mobile gets a same-tab replace flow that we haven't
    // built yet. Block here rather than silently malfunction in a narrow viewport.
    const isNarrow = typeof window !== 'undefined' && window.innerWidth < 900;

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !submitting) onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [submitting, onClose]);

    const reasonTooShort = reason.trim().length < 10;

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting || reasonTooShort || isNarrow) return;
        setSubmitting(true);
        setError(null);
        setFieldErrors({});

        try {
            const res = await saRequest<{
                otx: string;
                session_id: number;
                otx_expires_at: string;
            }>(
                'POST',
                `companies/${target.company_id}/users/${target.user_id}/impersonate`,
                { reason: reason.trim(), duration_minutes: duration },
            );
            const { otx } = res.data;

            // Open the destination tab first — browsers block window.open if
            // it isn't inside the click gesture. The landing route reads ?otx
            // and finishes the exchange.
            const win = window.open(
                `/impersonation/landing?otx=${encodeURIComponent(otx)}`,
                '_blank',
                'noopener',
            );
            if (win === null) {
                setError(
                    'Your browser blocked the new tab. Allow popups for this site and try again.',
                );
                setSubmitting(false);
                return;
            }
            onClose();
        } catch (err: any) {
            const p = parseSuperAdminError(err);
            setError(p.banner);
            setFieldErrors(p.fields);
            setSubmitting(false);
        }
    };

    return (
        <div className="sa-modal-backdrop" onClick={submitting ? undefined : onClose}>
            <div
                className="sa-modal"
                onClick={e => e.stopPropagation()}
                style={{ maxWidth: 520 }}
            >
                <h2>
                    Impersonate{' '}
                    <span style={{ color: 'var(--sa-accent)' }}>
                        {target.user_fullname || target.user_email || `#${target.user_id}`}
                    </span>
                </h2>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        columnGap: 'var(--sa-s3)',
                        rowGap: 4,
                        fontSize: 'var(--sa-fs-sm)',
                        margin: '0 0 var(--sa-s4)',
                    }}
                >
                    <span style={{ color: 'var(--sa-text-tertiary)' }}>User</span>
                    <span className="sa-mono" style={{ color: 'var(--sa-text)' }}>
                        {target.user_email}
                    </span>
                    <span style={{ color: 'var(--sa-text-tertiary)' }}>Company</span>
                    <span style={{ color: 'var(--sa-text)' }}>{target.company_name}</span>
                </div>

                {target.is_tenant_admin && (
                    <div
                        style={{
                            padding: 'var(--sa-s3)',
                            background: 'rgba(251, 113, 133, 0.08)',
                            border: '1px solid rgba(251, 113, 133, 0.24)',
                            borderRadius: 'var(--sa-radius)',
                            fontSize: 'var(--sa-fs-xs)',
                            color: 'var(--sa-text-secondary)',
                            margin: '0 0 var(--sa-s4)',
                        }}
                    >
                        This is a tenant-admin account. You'll see everything they see —
                        including other users' data under their scope.
                    </div>
                )}

                <p
                    style={{
                        color: 'var(--sa-text-secondary)',
                        fontSize: 'var(--sa-fs-sm)',
                        margin: '0 0 var(--sa-s4)',
                    }}
                >
                    The session is <strong style={{ color: 'var(--sa-text)' }}>read-only</strong>
                    {' '}— every write is blocked server-side and audited. A persistent red banner
                    will follow you until the session ends.
                </p>

                {error && <div className="sa-error-banner">{error}</div>}

                {isNarrow && (
                    <div className="sa-error-banner">
                        Impersonation requires a desktop-width browser. Mobile support is
                        coming in a later release.
                    </div>
                )}

                <form onSubmit={onSubmit} noValidate>
                    <div className="sa-field">
                        <label>
                            Reason{' '}
                            <span
                                style={{
                                    color: 'var(--sa-text-tertiary)',
                                    fontWeight: 400,
                                    textTransform: 'none',
                                }}
                            >
                                — min 10 chars, stored in the audit log
                            </span>
                        </label>
                        <textarea
                            rows={3}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            disabled={submitting || isNarrow}
                            placeholder="e.g. reproducing export crash reported in support ticket #4721"
                            autoFocus
                        />
                        {fieldErrors.reason && (
                            <div className="sa-field-error">{fieldErrors.reason}</div>
                        )}
                    </div>

                    <div className="sa-field">
                        <label>Duration</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {DURATIONS.map(d => (
                                <button
                                    key={d.value}
                                    type="button"
                                    onClick={() => setDuration(d.value)}
                                    disabled={submitting || isNarrow}
                                    className={`sa-btn sa-btn-sm ${
                                        duration === d.value
                                            ? 'sa-btn-primary'
                                            : 'sa-btn-secondary'
                                    }`}
                                    style={{ minWidth: 72 }}
                                >
                                    {d.label}
                                </button>
                            ))}
                        </div>
                        {fieldErrors.duration_minutes && (
                            <div className="sa-field-error">
                                {fieldErrors.duration_minutes}
                            </div>
                        )}
                    </div>

                    <div className="sa-modal-actions">
                        <button
                            type="button"
                            className="sa-btn sa-btn-ghost"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="sa-btn sa-btn-primary"
                            disabled={submitting || reasonTooShort || isNarrow}
                        >
                            {submitting && <span className="sa-btn-spinner" />}
                            {submitting ? 'Opening session…' : 'Begin impersonation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ImpersonateModal;
